import { Injectable } from '@angular/core';

/**
 * Thin wrapper around the browser's sessionStorage API that adds
 * automatic JSON serialization/deserialization and error resilience.
 *
 * Exists as a separate injectable so that tests can mock storage
 * without touching the global sessionStorage object, and so that
 * storage errors (e.g. quota exceeded, private-browsing restrictions)
 * are caught in one place rather than crashing callers.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  public getItem(key: string): any {
    try {
      const raw = sessionStorage.getItem(key);
      // Tolerate legacy state: prior versions could write the literal string
      // "undefined" via setItem(k, undefined) → JSON.stringify(undefined) →
      // browser-coerced to "undefined". JSON.parse would then throw.
      if (raw === null || raw === 'undefined') return null;
      return JSON.parse(raw);
    } catch (e) {
      console.error('SessionStorageService', e);
      return null;
    }
  }

  public setItem(key: string, item: any): void {
    try {
      // Storing undefined would round-trip through JSON.stringify (→ undefined)
      // and sessionStorage's string coercion to land as the unparseable string
      // "undefined". Treat it as a removal instead so the next getItem returns
      // null cleanly.
      if (item === undefined) {
        sessionStorage.removeItem(key);
        return;
      }
      sessionStorage.setItem(key, JSON.stringify(item));
    } catch (e) {
      console.error('SessionStorageService', e);
    }
  }

  public removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.error('SessionStorageService', e);
    }
  }

  public removeAll(): void {
    try {
      sessionStorage.clear();
    } catch (e) {
      console.error('SessionStorageService', e);
    }
  }
}
